import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const CART_DRAWER_ID = 'beybling-cart-drawer';
/** @type {'beybling:cart-drawer-reflow'} */
const CART_DRAWER_REFLOW_EVENT = 'beybling:cart-drawer-reflow';
const LOCALE_SWITCH_KEY = 'beybling-locale-switch';
const THEME_STORAGE_KEY = 'beybling-theme';
const DESKTOP_MEDIA = window.matchMedia('(min-width: 750px)');

/** @type {HTMLElement | null} */
let localeSwitchOverlay = null;

/** @type {number} */
let currentCartCount = 0;

/** @type {boolean} */
let cartDrawerRefreshPending = false;

/** @type {Promise<void> | null} */
let cartSyncPromise = null;

/** @type {HTMLButtonElement | null} */
let lastCartTrigger = null;

/**
 * @returns {number | null}
 */
function readStoredCartCount() {
  try {
    const stored = sessionStorage.getItem('cart-count');
    if (!stored) return null;

    const { value, timestamp } = JSON.parse(stored);
    if (Date.now() - timestamp >= 86400000) return null;

    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) || parsed < 0 ? null : parsed;
  } catch {
    return null;
  }
}

/**
 * Server-rendered count is authoritative on page load.
 *
 * @returns {number}
 */
function readInitialCartCount() {
  const bodyCount = parseInt(document.body.dataset.cartCount ?? '', 10);
  if (!Number.isNaN(bodyCount) && bodyCount >= 0) {
    return bodyCount;
  }

  let maxCount = 0;
  document.querySelectorAll('.beybling-nav__badge').forEach((badge) => {
    if (!(badge instanceof HTMLElement) || badge.hidden) return;

    const parsed = parseInt(badge.textContent ?? '', 10);
    if (!Number.isNaN(parsed)) {
      maxCount = Math.max(maxCount, parsed);
    }
  });

  return maxCount;
}

/**
 * @returns {number}
 */
function readCartCountFromDom() {
  const countEl = document.querySelector(`#${CART_DRAWER_ID} [ref="cartItemCount"]`);
  if (countEl) {
    const parsed = parseInt(countEl.textContent?.trim() ?? '0', 10);
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
  }

  return readInitialCartCount();
}

/**
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
function getCartCountFromSections(sections) {
  if (!sections || typeof sections !== 'object') return null;

  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const countEl = doc.querySelector('[ref="cartItemCount"]');
    if (!countEl) continue;

    const parsed = parseInt(countEl.textContent?.trim() ?? '0', 10);
    if (!Number.isNaN(parsed) && parsed >= 0) return parsed;
  }

  return null;
}

/**
 * @returns {number}
 */
function getDomCartCount() {
  return currentCartCount;
}

/**
 * Cart count rendered inside the drawer section (source of truth for drawer sync).
 *
 * @returns {number | null}
 */
function readDrawerCartCount() {
  const countEl = document.querySelector(`#${CART_DRAWER_ID} [ref="cartItemCount"]`);
  if (!countEl) return null;

  const parsed = parseInt(countEl.textContent?.trim() ?? '', 10);
  return Number.isNaN(parsed) || parsed < 0 ? null : parsed;
}

/**
 * Refresh the drawer when its rendered count does not match the live cart count.
 *
 * @param {number} itemCount
 * @returns {boolean}
 */
function isCartDrawerStaleForCount(itemCount) {
  const drawerCount = readDrawerCartCount();
  if (drawerCount !== null && drawerCount !== itemCount) return true;
  if (drawerCount === null && itemCount > 0) return true;

  const dialog = document.querySelector(`#${CART_DRAWER_ID} .cart-drawer__dialog`);
  if (!(dialog instanceof HTMLElement)) return itemCount > 0;

  const isEmptyDom = dialog.classList.contains('cart-drawer--empty');
  return isEmptyDom !== (itemCount === 0);
}

/**
 * @returns {string | null}
 */
function getCartDrawerSectionId() {
  const cartItems = document.querySelector(`#${CART_DRAWER_ID} cart-items-component`);
  if (!(cartItems instanceof HTMLElement)) return null;

  return cartItems.dataset.sectionId ?? null;
}

/**
 * @returns {Promise<void>}
 */
async function refreshCartDrawer() {
  const sectionId = getCartDrawerSectionId();
  if (!sectionId || cartDrawerRefreshPending) return;

  cartDrawerRefreshPending = true;

  try {
    sectionRenderer.invalidateSectionCache(sectionId);
    await sectionRenderer.renderSection(sectionId, { cache: false });
    updateCartBadge(readCartCountFromDom());
    dispatchCartDrawerReflow();
  } catch (error) {
    console.error('Failed to refresh cart drawer', error);
  } finally {
    cartDrawerRefreshPending = false;
  }
}

/**
 * Section re-renders replace footer markup and drop inline wallet/viewport
 * styles — tell checkout + viewport listeners to re-measure after DOM settles.
 */
function dispatchCartDrawerReflow() {
  requestAnimationFrame(() => {
    document.dispatchEvent(new CustomEvent(CART_DRAWER_REFLOW_EVENT));
  });
}

/**
 * @param {number} itemCount
 * @returns {Promise<void>}
 */
async function syncCartDrawerIfNeeded(itemCount = getDomCartCount()) {
  if (!isCartDrawerStaleForCount(itemCount)) return;
  await refreshCartDrawer();
}

/**
 * Fetches live cart state from Shopify — required after BFCache restore because
 * the frozen DOM and data-cart-count no longer reflect the real cart.
 *
 * @returns {Promise<void>}
 */
async function syncCartFromServer() {
  if (cartSyncPromise) return cartSyncPromise;

  cartSyncPromise = (async () => {
    try {
      const cartJsUrl = Theme.routes.cart_js_url || `${Theme.routes.cart_url}.js`;
      const response = await fetch(cartJsUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = typeof cart.item_count === 'number' ? cart.item_count : 0;

      updateCartBadge(itemCount);
      await syncCartDrawerIfNeeded(itemCount);
    } catch (error) {
      console.error('Failed to sync cart from server', error);
    } finally {
      cartSyncPromise = null;
    }
  })();

  return cartSyncPromise;
}

/**
 * @param {number} itemCount
 */
function persistCartCount(itemCount) {
  sessionStorage.setItem(
    'cart-count',
    JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now(),
    })
  );
}

/**
 * @param {number} itemCount
 */
function updateCartBadge(itemCount) {
  const previousCount = currentCartCount;
  currentCartCount = itemCount;
  document.body.dataset.cartCount = String(itemCount);
  persistCartCount(itemCount);

  const triggers = document.querySelectorAll('[data-cart-trigger]');
  const badges = document.querySelectorAll('.beybling-nav__badge');

  badges.forEach((badge) => {
    if (!(badge instanceof HTMLElement)) return;

    if (itemCount > 0) {
      badge.textContent = itemCount < 100 ? String(itemCount) : '99+';
      badge.hidden = false;
      if (itemCount > previousCount) pulseBadge(badge);
    } else {
      badge.textContent = '';
      badge.hidden = true;
    }
  });

  triggers.forEach((trigger) => {
    if (!(trigger instanceof HTMLButtonElement)) return;

    const openLabel = trigger.dataset.labelOpen || 'Open cart';
    const countLabel = trigger.dataset.labelCount || 'items';
    trigger.setAttribute('aria-label', `${openLabel} — ${itemCount} ${countLabel}`);
  });
}

/**
 * @param {boolean} expanded
 */
function setCartTriggerExpanded(expanded) {
  document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
    if (trigger instanceof HTMLButtonElement) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  });
}

/**
 * @param {HTMLElement | null} nav
 * @param {boolean} hidden
 */
function setNavHiddenState(nav, hidden) {
  if (!(nav instanceof HTMLElement)) return;

  nav.setAttribute('aria-hidden', hidden ? 'true' : 'false');

  if (hidden) {
    nav.setAttribute('inert', '');
  } else {
    nav.removeAttribute('inert');
  }
}

function syncNavAccessibility() {
  const sideNav = document.querySelector('.beybling-side-nav');
  const bottomNav = document.querySelector('.beybling-bottom-nav');
  const isDesktop = DESKTOP_MEDIA.matches;

  setNavHiddenState(sideNav, !isDesktop);
  setNavHiddenState(bottomNav, isDesktop);
}

/**
 * Keep Shop nav highlight aligned with collection/product pages (not home).
 *
 * @param {string} path
 */
function normalizeNavPath(path) {
  const base = path.split('?')[0] || '/';
  if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1);
  return base || '/';
}

/**
 * @returns {string}
 */
function getTemplateName() {
  for (const className of document.body.classList) {
    if (className.startsWith('template-')) return className.slice('template-'.length);
  }
  return '';
}

function syncShopNavActiveState() {
  const template = getTemplateName();
  const currentPath = normalizeNavPath(window.location.pathname);

  document.querySelectorAll('.beybling-bottom-nav__item--shop, .beybling-side-nav__link--shop').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;

    const shopPath = normalizeNavPath(new URL(link.href, window.location.origin).pathname);
    const isActive =
      template === 'collection' || template === 'product' || currentPath === shopPath;

    link.classList.toggle('is-active', isActive);
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

/**
 * @returns {HTMLButtonElement | null}
 */
function getVisibleCartTrigger() {
  for (const trigger of document.querySelectorAll('[data-cart-trigger]')) {
    if (!(trigger instanceof HTMLButtonElement)) continue;

    const style = getComputedStyle(trigger);
    if (style.display === 'none' || style.visibility === 'hidden') continue;

    const rect = trigger.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return trigger;
  }

  return null;
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-cart-trigger]');
    if (trigger instanceof HTMLButtonElement) {
      lastCartTrigger = trigger;
    }
  },
  true
);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (isAddToCartSource(event)) {
    // Auto-open drawer already confirms the add; only toast when it stays closed
    // (e.g. quick-add without auto-open).
    requestAnimationFrame(() => {
      const dialog = document.querySelector(`#${CART_DRAWER_ID} .cart-drawer__dialog`);
      const drawerOpen = dialog instanceof HTMLDialogElement && dialog.open;
      if (!drawerOpen) showCartToast('Added to cart');
    });
  }

  const sections = event.detail?.data?.sections;
  const sectionsCount = getCartCountFromSections(sections);

  if (sectionsCount !== null) {
    updateCartBadge(sectionsCount);

    // CartUpdateEvent fires before morphSection — verify after the DOM settles.
    requestAnimationFrame(() => {
      syncCartDrawerIfNeeded(sectionsCount);
    });
    return;
  }

  const itemCount = event.detail?.data?.itemCount;
  if (typeof itemCount !== 'number') return;

  const comingFromProductForm = event.detail?.data?.source === 'product-form-component';
  const nextCount = comingFromProductForm ? currentCartCount + itemCount : itemCount;

  updateCartBadge(nextCount);

  requestAnimationFrame(() => {
    syncCartDrawerIfNeeded(nextCount);
  });
});

/**
 * Briefly animate a cart badge when the count grows.
 *
 * @param {HTMLElement} badge
 */
function pulseBadge(badge) {
  badge.classList.remove('beybling-nav__badge--pulse');
  // Force reflow so the animation can restart on rapid re-adds.
  void badge.offsetWidth;
  badge.classList.add('beybling-nav__badge--pulse');
  badge.addEventListener(
    'animationend',
    () => badge.classList.remove('beybling-nav__badge--pulse'),
    { once: true }
  );
}

/** @type {ReturnType<typeof setTimeout> | null} */
let toastTimer = null;

/**
 * Show a brief "added to cart" confirmation toast.
 *
 * @param {string} message
 */
function showCartToast(message) {
  let toast = document.getElementById('beybling-cart-toast');
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.id = 'beybling-cart-toast';
    toast.className = 'beybling-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  toast.textContent = message;

  // Restart enter animation if a toast is already showing.
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');

  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toast?.classList.remove('is-visible');
    toastTimer = null;
  }, 2200);
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function isAddToCartSource(event) {
  const source = /** @type {any} */ (event).detail?.data?.source;
  return typeof source === 'string' && (source.includes('product-form') || source.includes('quick-add'));
}

function bindCartDrawerA11y() {
  const drawer = document.getElementById(CART_DRAWER_ID);
  if (!(drawer instanceof HTMLElement)) return;

  drawer.addEventListener('dialog:open', () => {
    setCartTriggerExpanded(true);
    syncCartDrawerIfNeeded();
  });

  drawer.addEventListener('dialog:close', () => {
    setCartTriggerExpanded(false);

    const trigger = lastCartTrigger ?? getVisibleCartTrigger();
    trigger?.focus();
  });
}

/**
 * Sizes the cart drawer to the visible viewport (Safari bottom bar, URL bar)
 * and lifts the footer above the mobile keyboard while typing a discount code.
 */
function bindCartDrawerViewport() {
  const viewport = window.visualViewport;
  if (!viewport) return;

  const dialog = () => document.querySelector(`#${CART_DRAWER_ID} .cart-drawer__dialog`);

  const clearInlineViewport = () => {
    const element = dialog();
    if (!(element instanceof HTMLElement)) return;

    element.style.removeProperty('height');
    element.style.removeProperty('max-height');
    element.style.removeProperty('top');
    element.style.setProperty('--beybling-keyboard-offset', '0px');
  };

  const apply = () => {
    const element = dialog();
    if (!(element instanceof HTMLElement) || !element.open) return;

    // visualViewport.height is already the visible area above Safari chrome /
    // the keyboard — size the drawer to that, not to 100dvh.
    element.style.setProperty('height', `${Math.round(viewport.height)}px`, 'important');
    element.style.setProperty('max-height', `${Math.round(viewport.height)}px`, 'important');
    element.style.setProperty('top', `${Math.round(viewport.offsetTop)}px`, 'important');
    element.style.setProperty('--beybling-keyboard-offset', '0px');
  };

  // Coalesce bursts of visualViewport events (iOS keyboard/scroll can fire
  // dozens per second) into one style write per frame.
  let applyRafId = 0;
  const scheduleApply = () => {
    if (applyRafId) return;
    applyRafId = requestAnimationFrame(() => {
      applyRafId = 0;
      apply();
    });
  };

  viewport.addEventListener('resize', scheduleApply);
  viewport.addEventListener('scroll', scheduleApply);
  document.addEventListener(CART_DRAWER_REFLOW_EVENT, scheduleApply);

  const drawer = document.getElementById(CART_DRAWER_ID);
  drawer?.addEventListener('dialog:open', apply);
  drawer?.addEventListener('dialog:close', clearInlineViewport);
}

/**
 * Mark a product image's container as loaded so its skeleton shimmer stops.
 *
 * @param {HTMLImageElement} img
 */
function markMediaLoaded(img) {
  const container = img.closest('.product-media-container, .card-gallery');
  if (container instanceof HTMLElement) {
    container.classList.add('beybling-media-loaded');
  }
}

/**
 * Product grid images get a shimmer skeleton (CSS) until they finish loading.
 * `load` doesn't bubble, so we capture it at the document and also sweep images
 * already complete from cache.
 */
function bindImageSkeletons() {
  if (!document.querySelector('.product-media-container img, .card-gallery img')) return;

  document.addEventListener(
    'load',
    (event) => {
      if (event.target instanceof HTMLImageElement) markMediaLoaded(event.target);
    },
    true
  );

  document.querySelectorAll('.product-media-container img, .card-gallery img').forEach((img) => {
    if (img instanceof HTMLImageElement && img.complete && img.naturalWidth > 0) {
      markMediaLoaded(img);
    }
  });
}

/**
 * Open a fullscreen zoom lightbox for a product gallery image.
 * Self-contained — does not depend on the theme's (gutted) zoom dialog.
 *
 * @param {HTMLImageElement} sourceImg
 */
function openImageZoom(sourceImg) {
  /** @type {Element | null} */
  const previouslyFocused = document.activeElement;

  const overlay = document.createElement('div');
  overlay.className = 'beybling-zoom';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Product image zoom');

  const scroll = document.createElement('div');
  scroll.className = 'beybling-zoom__scroll';

  const big = document.createElement('img');
  big.className = 'beybling-zoom__img';
  // Prefer the full-resolution source the theme already exposes; fall back to
  // the responsive srcset so the browser still picks a large variant.
  const maxRes = sourceImg.dataset.maxResolution;
  if (maxRes) {
    big.src = maxRes;
  } else {
    if (sourceImg.srcset) big.srcset = sourceImg.srcset;
    big.src = sourceImg.currentSrc || sourceImg.src;
    big.sizes = '100vw';
  }
  big.alt = sourceImg.alt || '';
  big.decoding = 'async';
  // Fade the image in once it has loaded so the lightbox never shows a blank frame.
  const markLoaded = () => big.classList.add('is-loaded');
  big.addEventListener('load', markLoaded);
  big.addEventListener('error', () => {
    // Full-res source failed: fall back to the image already on the page.
    if (big.dataset.fallback) return markLoaded();
    big.dataset.fallback = '1';
    big.removeAttribute('srcset');
    big.src = sourceImg.currentSrc || sourceImg.src;
  });
  if (big.complete && big.naturalWidth > 0) markLoaded();

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'beybling-zoom__close';
  closeBtn.setAttribute('aria-label', 'Close zoom');
  closeBtn.textContent = '\u00D7';

  scroll.appendChild(big);
  overlay.appendChild(scroll);
  overlay.appendChild(closeBtn);
  document.body.appendChild(overlay);

  const previousOverflow = document.documentElement.style.overflow;
  document.documentElement.style.overflow = 'hidden';
  requestAnimationFrame(() => {
    overlay.classList.add('is-open');
    closeBtn.focus({ preventScroll: true });
  });

  const onKey = (event) => {
    if (event.key === 'Escape') {
      closeZoom();
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      closeBtn.focus({ preventScroll: true });
    }
  };

  function closeZoom() {
    overlay.classList.remove('is-open');
    document.documentElement.style.overflow = previousOverflow;
    document.removeEventListener('keydown', onKey);
    setTimeout(() => overlay.remove(), 220);

    if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
      previouslyFocused.focus({ preventScroll: true });
    }
  }

  document.addEventListener('keydown', onKey);
  closeBtn.addEventListener('click', closeZoom);
  overlay.addEventListener('pointerup', (event) => {
    if (event.pointerType === 'mouse') return;
    if (event.target === overlay || event.target === scroll) closeZoom();
  });
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay || event.target === scroll) closeZoom();
  });
  // Click to zoom in/out; while zoomed, the image pans with the pointer.
  const pan = (event) => {
    if (!big.classList.contains('is-zoomed')) return;
    const rect = big.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    big.style.transformOrigin = `${x}% ${y}%`;
  };
  // The <img> box can be wider than the visible picture (object-fit: contain), so test the real picture area.
  const outsidePicture = (event) => {
    const r = big.getBoundingClientRect();
    const nw = big.naturalWidth, nh = big.naturalHeight;
    if (!nw || !nh) return false;
    const s = Math.min(r.width / nw, r.height / nh);
    const w = nw * s, h = nh * s;
    const l = r.left + (r.width - w) / 2, tp = r.top + (r.height - h) / 2;
    return event.clientX < l || event.clientX > l + w || event.clientY < tp || event.clientY > tp + h;
  };
  big.addEventListener('click', (event) => {
    event.stopPropagation();
    if (outsidePicture(event)) {
      if (!big.dataset.bbPinched) closeZoom();
      return;
    }
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (!big.classList.contains('is-zoomed')) {
      const rect = big.getBoundingClientRect();
      big.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
    }
    const bbRect = big.getBoundingClientRect();
    const bbFx = (event.clientX - bbRect.left) / bbRect.width;
    const bbFy = (event.clientY - bbRect.top) / bbRect.height;
    big.classList.toggle('is-zoomed');
    if (window.matchMedia('(pointer: coarse)').matches) {
      const zoomedNow = big.classList.contains('is-zoomed');
      scroll.classList.toggle('is-zoomed', zoomedNow);
      if (zoomedNow) {
        requestAnimationFrame(() => {
          scroll.scrollLeft = bbFx * scroll.scrollWidth - scroll.clientWidth / 2;
          scroll.scrollTop = bbFy * scroll.scrollHeight - scroll.clientHeight / 2;
        });
      }
    }
  });
  big.addEventListener('pointermove', pan);
  // Touch: tap the un-zoomed photo to close; swipe down also closes.
  (function () {
    let sx = 0, sy = 0, st = 0, moved = false;
    big.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      sx = e.clientX; sy = e.clientY; st = Date.now(); moved = false;
    });
    big.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'mouse') return;
      if (Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8) moved = true;
    });
    big.addEventListener('pointerup', function (e) {
      if (e.pointerType === 'mouse') return;
      const tf = big.style.transform || '';
      const zoomed = /scale\((?!1\))/.test(tf);
      if (zoomed) return;
      const dy = e.clientY - sy;
      if ((!moved && Date.now() - st < 350) || (dy > 90 && Math.abs(e.clientX - sx) < 70)) closeZoom();
    });
  })();
  // Touch devices: tap opens full screen (already done), pinch to zoom, drag to pan when zoomed.
  if (window.matchMedia('(pointer: coarse)').matches) {
    const pts = new Map();
    let scale = 1, tx = 0, ty = 0;
    let startDist = 0, startScale = 1, startTx = 0, startTy = 0, startMid = { x: 0, y: 0 };
    let lastTap = 0;
    const apply = () => {
      big.style.transition = 'none';
      big.style.transformOrigin = '50% 50%';
      big.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
    };
    const clampPan = () => {
      const r = scroll.getBoundingClientRect();
      const w = big.offsetWidth * scale, h = big.offsetHeight * scale;
      const mx = Math.max(0, (w - r.width) / 2), my = Math.max(0, (h - r.height) / 2);
      tx = Math.min(mx, Math.max(-mx, tx));
      ty = Math.min(my, Math.max(-my, ty));
    };
    const center = () => {
      const r = scroll.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };
    const dist = () => {
      const [a, b] = [...pts.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const mid = () => {
      const [a, b] = [...pts.values()];
      return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    big.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { big.setPointerCapture(e.pointerId); } catch (_) {}
      if (pts.size === 2) {
        startDist = dist(); startScale = scale; startTx = tx; startTy = ty; startMid = mid();
      }
    });
    big.addEventListener('pointermove', (e) => {
      if (e.pointerType === 'mouse' || !pts.has(e.pointerId)) return;
      const prev = pts.get(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 2) {
        const c = center(), m = mid();
        const next = Math.min(5, Math.max(1, startScale * (dist() / startDist)));
        const k = next / startScale;
        tx = (m.x - c.x) - ((startMid.x - c.x) - startTx) * k;
        ty = (m.y - c.y) - ((startMid.y - c.y) - startTy) * k;
        scale = next;
        big.dataset.bbPinched = '1';
        clampPan(); apply();
      } else if (pts.size === 1 && scale > 1) {
        tx += e.clientX - prev.x;
        ty += e.clientY - prev.y;
        clampPan(); apply();
      }
    });
    const end = (e) => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      if (big.dataset.bbPinched) setTimeout(() => { delete big.dataset.bbPinched; }, 400);
      if (pts.size === 1) {
        startScale = scale; startTx = tx; startTy = ty;
      }
      if (scale < 1.02) { scale = 1; tx = 0; ty = 0; apply(); }
    };
    big.addEventListener('pointerup', end);
    big.addEventListener('pointercancel', end);
    // Double tap resets zoom.
    big.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'mouse') return;
      const now = Date.now();
      if (now - lastTap < 300 && pts.size === 0 && scale > 1) { scale = 1; tx = 0; ty = 0; big.style.transition = 'transform 0.2s ease'; apply(); big.style.transition = 'transform 0.2s ease'; }
      lastTap = now;
    });
  }

}

/**
 * Click-to-zoom on the product detail gallery (mobile slideshow + desktop main
 * image). Thumbnails, videos and 3D models are excluded.
 */
function bindProductZoom() {
  // Delegated on the document so it survives product-section re-renders (variant changes), and it also
  // resolves clicks that land on an overlay (zoom button) instead of the <img> itself.
  if (window.__bbProductZoomBound) return;
  window.__bbProductZoomBound = true;

  let tapStart = null;
  let lastSlideScroll = 0;
  document.addEventListener('pointerdown', (e) => {
    const slides = e.target instanceof Element ? e.target.closest('slideshow-slides') : null;
    tapStart = { x: e.clientX, y: e.clientY, left: slides ? slides.scrollLeft : 0, slides };
  }, { passive: true, capture: true });
  document.addEventListener('scroll', (e) => {
    if (e.target instanceof Element && e.target.closest('.product-information')) lastSlideScroll = Date.now();
  }, { capture: true, passive: true });
  function wasSwipe(e) {
    if (Date.now() - lastSlideScroll < 350) return true;
    if (!tapStart) return false;
    const moved = Math.hypot(e.clientX - tapStart.x, e.clientY - tapStart.y) > 10;
    const scrolled = tapStart.slides && Math.abs(tapStart.slides.scrollLeft - tapStart.left) > 4;
    return moved || scrolled;
  }

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element) || !t.closest('.product-information')) return;
    if (t.closest('.thumbnail-item, slideshow-arrows, slideshow-controls, .deferred-media__poster-button, video')) return;
    if (wasSwipe(event)) return;

    let img = t instanceof HTMLImageElement && t.classList.contains('product-media__image') ? t : null;
    if (!img) {
      // Click landed on an overlay/wrapper: find the visible main photo it covers.
      const desktop = t.closest('.main-image-container');
      const mobile = t.closest('.mobile-slideshow-layout slideshow-slide, .mobile-slideshow-layout .product-media-container');
      if (desktop) img = desktop.querySelector('.main-image-item.active img.product-media__image') || desktop.querySelector('img.product-media__image');
      else if (mobile) img = mobile.querySelector('img.product-media__image');
    }
    if (!(img instanceof HTMLImageElement)) return;
    if (!img.closest('.main-image-container') && !img.closest('.mobile-slideshow-layout')) return;

    event.preventDefault();
    openImageZoom(img);
  });
}

/**
 * Snappy beyblade spin on logo links and the Shop nav gimmick icon.
 */
function bindLogoSpin() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  document
    .querySelectorAll(
      '.beybling-side-nav__logo, .beybling-announcement__logo, .beybling-bottom-nav__item--shop, .beybling-side-nav__link--shop'
    )
    .forEach((link) => {
      if (!(link instanceof HTMLElement)) return;
      if (link.dataset.beyblingLogoSpinBound === 'true') return;
      link.dataset.beyblingLogoSpinBound = 'true';

      link.addEventListener(
        'pointerdown',
        (event) => {
          if (event.button !== 0) return;

          const spinTarget =
            link.querySelector('.beybling-side-nav__shop-o') ||
            link.querySelector('.beybling-nav__shop-icon, img') ||
            link;
          if (!(spinTarget instanceof HTMLElement)) return;

          spinTarget.getAnimations().forEach((anim) => anim.cancel());

          // Preserve any CSS-applied filter (e.g. the dark-mode logo invert) so the
          // spin's brightness/drop-shadow layers on top instead of replacing it —
          // otherwise the logo visibly flips to its un-inverted colors mid-spin.
          const baseFilter = getComputedStyle(spinTarget).filter;
          const filterPrefix = baseFilter && baseFilter !== 'none' ? `${baseFilter} ` : '';

          spinTarget.animate(
            [
              { transform: 'rotate(0deg) scale(1)', filter: baseFilter },
              { transform: 'rotate(200deg) scale(1.14)', filter: baseFilter, offset: 0.45 },
              { transform: 'rotate(380deg) scale(0.97)', filter: baseFilter, offset: 0.8 },
              { transform: 'rotate(360deg) scale(1)', filter: baseFilter },
],
{
duration: 720,
easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
fill: 'none',
}
);
        },
        { passive: true }
      );
    });
}

/**
 * Quick icon spin on nav tap (bottom bar + side links with SVG icons).
 */
function bindNavIconTap() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) return;

  document.querySelectorAll('.beybling-bottom-nav__item, .beybling-side-nav__link:not(.beybling-side-nav__link--shop)').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;

    item.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0) return;

        const icon = item.querySelector('svg');
        if (!(icon instanceof SVGElement)) return;

        icon.getAnimations().forEach((anim) => anim.cancel());
        icon.animate(
          [
            { transform: 'rotate(0deg) scale(1)' },
            { transform: 'rotate(72deg) scale(1.08)' },
            { transform: 'rotate(0deg) scale(1)' },
          ],
          {
            duration: 340,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'none',
          }
        );
      },
      { passive: true }
    );
  });
}

function syncLanguageForCurrencyForm(form) {
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('beybling-locale__dropdown-form--currency')) {
    return;
  }

  const countryField = form.querySelector('[name="country_code"]');
  const languageInput = form.querySelector('[name="language_code"]');
  if (!(languageInput instanceof HTMLInputElement)) {
    return;
  }

  let language = 'en';

  if (countryField instanceof HTMLSelectElement) {
    const option = countryField.options[countryField.selectedIndex];
    const label = option?.text?.trim().toUpperCase();
    language = option?.dataset.language || (label === 'JPY' ? 'ja' : 'en');
  } else if (countryField instanceof HTMLInputElement) {
    const activePill = form.querySelector('[data-beybling-currency-pill].is-active');
    if (activePill instanceof HTMLButtonElement) {
      const label = activePill.textContent?.trim().toUpperCase();
      language = activePill.dataset.language || (label === 'JPY' ? 'ja' : 'en');
    }
  }

  languageInput.value = language;
}

function syncLocaleTriggerLabel(select) {
  if (!(select instanceof HTMLSelectElement)) return;
  const form = select.closest('form');
  if (!(form instanceof HTMLFormElement)) return;
  const trigger = form.querySelector('[data-beybling-locale-trigger]');
  if (!(trigger instanceof HTMLButtonElement)) return;
  const option = select.options[select.selectedIndex];
  trigger.textContent = option?.text?.trim() || select.value.toUpperCase();
}

function bindLocalePickerTriggers() {
  document.querySelectorAll('[data-beybling-locale-trigger]').forEach((trigger) => {
    if (!(trigger instanceof HTMLButtonElement)) return;
    if (trigger.dataset.beyblingLocaleTriggerBound === 'true') return;
    trigger.dataset.beyblingLocaleTriggerBound = 'true';

    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const form = trigger.closest('form');
      if (!(form instanceof HTMLFormElement)) return;
      const select = form.querySelector('.beybling-locale-picker__select-native');
      if (!(select instanceof HTMLSelectElement) || select.disabled) return;

      try {
        if (typeof select.showPicker === 'function') {
          select.showPicker();
          return;
        }
      } catch {
        /* fall through */
      }

      select.focus({ preventScroll: true });
      select.click();
    });
  });
}

/**
 * @param {HTMLFormElement} form
 * @param {string} label
 */
function submitCurrencyLocalization(form, label) {
  if (form.dataset.beyblingLocalizationSubmitting === 'true') return;

  syncLanguageForCurrencyForm(form);

  form.classList.add('is-submitting');
  form.dataset.beyblingLocalizationSubmitting = 'true';

  try {
    sessionStorage.setItem(
      LOCALE_SWITCH_KEY,
      JSON.stringify({ label, type: 'currency', ts: Date.now() })
    );
  } catch {
    /* no-op */
  }

  showLocaleSwitchOverlay(label, 'currency');

  const body = new FormData(form);
  const action = form.getAttribute('action') || '/localization';

  fetch(action, {
    method: 'POST',
    body,
    credentials: 'same-origin',
    redirect: 'follow',
    keepalive: true,
  })
    .then((response) => {
      window.location.replace(response.url);
    })
    .catch(() => {
      hideLocaleSwitchOverlay();
      form.classList.remove('is-submitting');
      delete form.dataset.beyblingLocalizationSubmitting;
      HTMLFormElement.prototype.submit.call(form);
    });
}

function bindCurrencyPills(form) {
  const countryInput = form.querySelector('[name="country_code"]');
  if (!(countryInput instanceof HTMLInputElement)) return;

  form.querySelectorAll('[data-beybling-currency-pill]').forEach((pill) => {
    if (!(pill instanceof HTMLButtonElement)) return;
    if (pill.dataset.beyblingCurrencyPillBound === 'true') return;
    pill.dataset.beyblingCurrencyPillBound = 'true';

    pill.addEventListener('click', () => {
      if (pill.classList.contains('is-active')) return;
      if (form.dataset.beyblingLocalizationSubmitting === 'true') return;

      countryInput.value = pill.dataset.country || countryInput.value;

      form.querySelectorAll('[data-beybling-currency-pill]').forEach((option) => {
        if (!(option instanceof HTMLButtonElement)) return;
        option.classList.remove('is-active');
        option.setAttribute('aria-pressed', 'false');
      });

      pill.classList.add('is-active');
      pill.setAttribute('aria-pressed', 'true');

      syncLanguageForCurrencyForm(form);
      submitCurrencyLocalization(form, pill.textContent?.trim() || countryInput.value.toUpperCase());
    });
  });
}

function bindLocalizationForms() {
  document.querySelectorAll('form.beybling-locale__dropdown-form--currency').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.beyblingLocalizationBound === 'true') return;
    form.dataset.beyblingLocalizationBound = 'true';

    syncLanguageForCurrencyForm(form);
    bindCurrencyPills(form);

    const select = form.querySelector('.beybling-locale-picker__select-native, .beybling-locale__select');
    if (select instanceof HTMLSelectElement) {
      select.dataset.beyblingLocaleValue = select.value;
      syncLocaleTriggerLabel(select);
      select.addEventListener('change', () => {
        syncLanguageForCurrencyForm(form);
        syncLocaleTriggerLabel(select);
        if (select.value === select.dataset.beyblingLocaleValue) return;
        select.dataset.beyblingLocaleValue = select.value;
        const label = select.options[select.selectedIndex]?.text?.trim() || select.value.toUpperCase();
        submitCurrencyLocalization(form, label);
      });
    }
  });
}

/**
 * @returns {HTMLElement}
 */
function createLocaleSwitchOverlay() {
  const overlay = document.createElement('div');
  overlay.className = 'beybling-locale-overlay';
  overlay.setAttribute('aria-live', 'polite');
  overlay.setAttribute('aria-busy', 'true');

  const panel = document.createElement('div');
  panel.className = 'beybling-locale-overlay__panel';

  const logo = document.createElement('img');
  logo.className = 'beybling-locale-overlay__logo';
  logo.alt = '';
  logo.width = 72;
  logo.height = 38;
  logo.hidden = true;

  const label = document.createElement('p');
  label.className = 'beybling-locale-overlay__label';

  const track = document.createElement('div');
  track.className = 'beybling-locale-overlay__track';
  track.setAttribute('aria-hidden', 'true');

  const fill = document.createElement('span');
  fill.className = 'beybling-locale-overlay__fill';
  track.appendChild(fill);

  panel.appendChild(logo);
  panel.appendChild(label);
  panel.appendChild(track);
  overlay.appendChild(panel);

  return overlay;
}

/**
 * @param {string} label
 * @param {'currency' | 'language'} switchType
 */
function showLocaleSwitchOverlay(label, switchType) {
  document.documentElement.classList.add('beybling-locale-switching');
  document.body.style.cursor = 'wait';

  if (!localeSwitchOverlay) {
    localeSwitchOverlay = createLocaleSwitchOverlay();
    document.body.appendChild(localeSwitchOverlay);
  }

  const logoEl = localeSwitchOverlay.querySelector('.beybling-locale-overlay__logo');
  const labelEl = localeSwitchOverlay.querySelector('.beybling-locale-overlay__label');
  const navLogo = document.querySelector(
    '.beybling-side-nav__logo .beybling-brand-logo--default, .beybling-announcement__logo .beybling-brand-logo--default, .beybling-side-nav__logo img, .beybling-announcement__logo img'
  );
  const isDark = document.documentElement.getAttribute('data-beybling-theme') === 'dark';
  const logoSrc = navLogo instanceof HTMLImageElement ? navLogo.src : '';

  if (logoEl instanceof HTMLImageElement && logoSrc) {
    logoEl.src = logoSrc;
    logoEl.hidden = false;
    logoEl.style.filter = isDark ? 'invert(1) grayscale(1) contrast(1.08)' : '';
  } else if (logoEl instanceof HTMLImageElement) {
    logoEl.hidden = true;
    logoEl.style.filter = '';
  }

  if (labelEl) {
    const prefix = switchType === 'currency' ? 'Switching to' : 'Loading';
    labelEl.textContent = label ? `${prefix} ${label}` : 'Updating…';
  }

  localeSwitchOverlay.hidden = false;
  requestAnimationFrame(() => {
    localeSwitchOverlay?.classList.add('is-visible');
  });
}

function hideLocaleSwitchOverlay() {
  document.documentElement.classList.remove('beybling-locale-switching');
  document.body.style.cursor = '';

  if (!localeSwitchOverlay) return;
  localeSwitchOverlay.classList.remove('is-visible');
  localeSwitchOverlay.hidden = true;
}

function playLocaleEnterAnimation() {
  try {
    const raw = sessionStorage.getItem(LOCALE_SWITCH_KEY);
    if (!raw) return;

    sessionStorage.removeItem(LOCALE_SWITCH_KEY);
    const data = JSON.parse(raw);
    if (!data?.ts || Date.now() - data.ts > 8000) return;

    document.documentElement.classList.add('beybling-locale-enter');
    requestAnimationFrame(() => {
      document.documentElement.classList.add('beybling-locale-enter--done');
    });

    window.setTimeout(() => {
      document.documentElement.classList.remove('beybling-locale-enter', 'beybling-locale-enter--done');
    }, 360);
  } catch {
    /* no-op */
  }
}

/**
 * @returns {'light' | 'dark'}
 */
function getStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* no-op */
  }

  return 'light';
}

/**
 * @param {'light' | 'dark'} theme
 */
function applyTheme(theme) {
  if (document.documentElement.getAttribute('data-beybling-theme') !== theme) {
    document.documentElement.setAttribute('data-beybling-theme', theme);
  }

  document.querySelectorAll('[data-beybling-theme-toggle]').forEach((toggle) => {
    if (!(toggle instanceof HTMLButtonElement)) return;

    toggle.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'
    );
  });
}

function bindThemeToggle() {
  document.querySelectorAll('[data-beybling-theme-toggle]').forEach((toggle) => {
    if (!(toggle instanceof HTMLButtonElement) || toggle.dataset.beyblingThemeBound === 'true') {
      return;
    }

    toggle.dataset.beyblingThemeBound = 'true';
    toggle.addEventListener('click', () => {
      const current = document.documentElement.getAttribute('data-beybling-theme') === 'dark' ? 'dark' : 'light';
      const next = current === 'dark' ? 'light' : 'dark';

      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* no-op */
      }

      applyTheme(next);
    });
  });
}

function initTheme() {
  applyTheme(getStoredTheme());
  bindThemeToggle();
}

function initStickyProductAtc() {
  if (!document.body.classList.contains('template-product')) return;

  const bar = document.querySelector('[data-beybling-sticky-atc]');
  const buyButtons = document.querySelector('.product-form-buttons');
  const trigger = document.querySelector('[data-beybling-sticky-atc-trigger]');
  const priceEl = document.querySelector('[data-beybling-sticky-price]');

  if (!(bar instanceof HTMLElement) || !(buyButtons instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) {
    return;
  }

  const mobileMedia = window.matchMedia('(max-width: 749px)');

  const setVisible = (visible) => {
    if (!mobileMedia.matches) {
      bar.hidden = true;
      bar.classList.remove('is-visible');
      return;
    }

    bar.hidden = !visible;
    bar.classList.toggle('is-visible', visible);
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      setVisible(!entry.isIntersecting);
    },
    { threshold: 0, rootMargin: '0px 0px -8px 0px' }
  );

  observer.observe(buyButtons);

  const onMediaChange = () => {
    if (!mobileMedia.matches) {
      setVisible(false);
      return;
    }
    const rect = buyButtons.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    setVisible(!inView);
  };

  if (typeof mobileMedia.addEventListener === 'function') {
    mobileMedia.addEventListener('change', onMediaChange);
  } else {
    mobileMedia.addListener(onMediaChange);
  }

  trigger.addEventListener('click', () => {
    const mainButton = buyButtons.querySelector('button[name="add"]:not([disabled])');
    if (mainButton instanceof HTMLButtonElement) {
      mainButton.click();
    }
  });

  trigger.dataset.labelAvailable = trigger.textContent.trim();

  document.addEventListener('variant:update', (event) => {
    if (!(event instanceof CustomEvent)) return;

    const variant = event.detail?.resource;
    if (!variant) return;

    const livePrice = document.querySelector('.product-information product-price, .product-information .price');
    if (priceEl instanceof HTMLElement && livePrice instanceof HTMLElement) {
      priceEl.textContent = livePrice.textContent.trim();
    }

    trigger.disabled = !variant.available;
    trigger.textContent = variant.available
      ? trigger.dataset.labelAvailable || 'Add to cart'
      : 'Sold out';
  });
}

/**
 * Home hero slideshow can paint two slides at once before the theme slideshow
 * component snaps scrollLeft — reset to the active slide on load and resize.
 */
function bindHomeHeroSlideshow() {
  const heroes = document.querySelectorAll('.beybling-home-hero__slideshow');
  if (!heroes.length) return;

  const snapHomeHero = (root) => {
    const scroller = root.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const active =
      root.querySelector('slideshow-slide[aria-hidden="false"]') ||
      root.querySelector('slideshow-slide:not([hidden])') ||
      scroller.querySelector('slideshow-slide');
    if (!(active instanceof HTMLElement)) return;

    const targetLeft = active.offsetLeft;
    if (Math.abs(scroller.scrollLeft - targetLeft) > 2) {
      scroller.scrollTo({ left: targetLeft, behavior: 'auto' });
    }
  };

  heroes.forEach((root) => {
    if (!(root instanceof HTMLElement) || root.dataset.beyblingHomeHeroReady === 'true') return;
    root.dataset.beyblingHomeHeroReady = 'true';

    const scroller = root.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement)) return;

    const scheduleSnap = () => requestAnimationFrame(() => snapHomeHero(root));

    scheduleSnap();
    window.setTimeout(scheduleSnap, 120);
    window.setTimeout(scheduleSnap, 480);

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(scheduleSnap);
      ro.observe(scroller);
    } else {
      window.addEventListener('resize', scheduleSnap, { passive: true });
    }

    root.querySelectorAll('slideshow-slide').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const mo = new MutationObserver(scheduleSnap);
      mo.observe(slide, { attributes: true, attributeFilter: ['aria-hidden', 'hidden'] });
    });
  });
}

/**
 * Swap the product description when the belt option (Premium / Standard) changes.
 * The variant picker only re-renders itself, so pull the description from the
 * freshly fetched section HTML.
 */
function bindVariantDescription() {
  document.addEventListener('variant:update', (event) => {
    

    const html = event.detail?.data?.html;
    if (!html) return;

    const next = html.querySelector('[data-variant-description]');
    const current = document.querySelector('[data-variant-description]');
    if (next && current) current.replaceWith(document.importNode(next, true));
  });
}

function init() {
  playLocaleEnterAnimation();
  initTheme();
  currentCartCount = readInitialCartCount();
  updateCartBadge(currentCartCount);
  bindCartDrawerA11y();
  bindCartDrawerViewport();
  bindImageSkeletons();
  bindProductZoom();
  bindVariantDescription();
  bindLogoSpin();
  bindNavIconTap();
  bindLocalizationForms();
  bindLocalePickerTriggers();
  syncNavAccessibility();
  syncShopNavActiveState();
  initStickyProductAtc();
  bindHomeHeroSlideshow();

  if (typeof DESKTOP_MEDIA.addEventListener === 'function') {
    DESKTOP_MEDIA.addEventListener('change', syncNavAccessibility);
  } else {
    DESKTOP_MEDIA.addListener(syncNavAccessibility);
  }

  requestAnimationFrame(() => {
    syncCartDrawerIfNeeded(currentCartCount);
  });
}

document.addEventListener('shopify:section:load', () => {
  bindLocalizationForms();
  bindLocalePickerTriggers();
  bindThemeToggle();
  bindLogoSpin();
  bindHomeHeroSlideshow();
  syncNavAccessibility();
  syncShopNavActiveState();
});

window.addEventListener('pageshow', (event) => {
  syncShopNavActiveState();

  if (event.persisted) {
    syncCartFromServer();
    return;
  }

  const storedCount = readStoredCartCount();
  const bodyCount = parseInt(document.body.dataset.cartCount ?? '0', 10);

  if (storedCount !== null && storedCount !== bodyCount) {
    syncCartFromServer();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Nav markup renders before this module; sync aria-hidden/inert immediately.
syncNavAccessibility();

/* bb-variant-description: show only the description block that matches the selected pack */
(function () {
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
  function selected() {
    var el = document.querySelector('variant-picker input:checked, fieldset input[type="radio"]:checked');
    if (el) return norm(el.value);
    var o = document.querySelector('variant-picker select option:checked, select[name^="options"] option:checked');
    return o ? norm(o.value || o.textContent) : '';
  }
  function sync() {
    var blocks = document.querySelectorAll('.bb-desc[data-bb-pack]');
    if (!blocks.length) return;
    var s = selected();
    var any = false;
    blocks.forEach(function (b) { if (s && s.indexOf(norm(b.getAttribute('data-bb-pack'))) === 0) any = true; });
    blocks.forEach(function (b) {
      var show = !any || s.indexOf(norm(b.getAttribute('data-bb-pack'))) === 0;
      b.style.display = show ? '' : 'none';
    });
  }
  function later() { sync(); setTimeout(sync, 120); setTimeout(sync, 500); }
  document.addEventListener('change', later, true);
  document.addEventListener('click', later, true);
  document.addEventListener('variant:update', later);
  document.addEventListener('DOMContentLoaded', later);
  window.addEventListener('load', later);
  window.addEventListener('popstate', later);
  later();
})();


/* Product gallery: autoplay the video slide (muted, looping) when it becomes the active photo. */
(function () {
  function activate(slide) {
    if (!slide || slide.getAttribute('aria-hidden') === 'true') return;
    var dm = slide.querySelector('deferred-media');
    if (!dm) return;
    var video = dm.querySelector('video');
    if (!video) {
      var btn = dm.querySelector('.deferred-media__poster-button');
      if (btn) btn.click();
      return;
    }
    video.muted = true;
    video.loop = true;
    var p = video.play();
    if (p && p.catch) p.catch(function () {});
  }
  function pauseHidden(slide) {
    var video = slide.querySelector('deferred-media video');
    if (video && slide.getAttribute('aria-hidden') === 'true') video.pause();
  }
  function observe(root) {
    root.querySelectorAll('slideshow-slide').forEach(function (s) {
      if (s.__bbVid) return;
      if (!s.querySelector('deferred-media') || s.closest('dialog')) return;
      s.__bbVid = true;
      new MutationObserver(function () {
        if (s.getAttribute('aria-hidden') === 'false') activate(s); else pauseHidden(s);
      }).observe(s, { attributes: true, attributeFilter: ['aria-hidden'] });
      activate(s);
    });
  }
  function init() {
    observe(document);
    new MutationObserver(function () { observe(document); }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();


/* Quick-add button: spin the logo when it is clicked. */
(function () {
  if (window.__bbQuickAddSpin) return;
  window.__bbQuickAddSpin = true;
  document.addEventListener('click', function (event) {
    var btn = event.target instanceof Element ? event.target.closest('.product-card .quick-add__button') : null;
    if (!btn || !btn.animate) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (btn.__bbSpinning) return;
    btn.__bbSpinning = true;
    var anim = btn.animate(
      [{ transform: 'rotate(0deg) scale(1)' }, { transform: 'rotate(180deg) scale(1.12)', offset: 0.5 }, { transform: 'rotate(360deg) scale(1)' }],
      { duration: 520, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    var done = function () { btn.__bbSpinning = false; };
    anim.onfinish = done;
    anim.oncancel = done;
  }, true);
})();


/* Logo confetti: a burst of logo-shaped pieces pops around Shop, Pre-order/Add to cart, checkout and quick-add buttons.
   Smoothness: the tinted logo is pre-rendered once to a small image (no CSS masks per piece), pieces only animate
   transform + opacity on their own compositor layers, and the piece count is lower on phones. */
(function () {
  if (window.__bbLogoConfetti) return;
  window.__bbLogoConfetti = true;
  var SELECTOR = '.quick-add__button, .add-to-cart-button, button[name="add"], .beybling-sticky-atc__button, .bb-upsell__btn, .bb-pop__submit, .cart__checkout-button, button[name="checkout"], .beybling-side-nav__link[href*="/collections/shop"], .beybling-bottom-nav__item[href*="/collections/shop"]';
  var last = 0;
  var tinted = {};
  var logoImg = null;

  function logoUrl() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--beybling-gimmick-logo-url').trim();
    var m = raw.match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/);
    return m ? m[1] : '';
  }

  function tint(color) {
    if (tinted[color]) return tinted[color];
    if (!logoImg || !logoImg.complete || !logoImg.naturalWidth) return '';
    try {
      var size = 72;
      var c = document.createElement('canvas');
      c.width = size; c.height = size;
      var ctx = c.getContext('2d');
      ctx.drawImage(logoImg, 0, 0, size, size);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, size, size);
      tinted[color] = c.toDataURL('image/png');
    } catch (e) { tinted[color] = ''; }
    return tinted[color];
  }

  function prepare() {
    if (logoImg) return;
    var url = logoUrl();
    if (!url) return;
    logoImg = new Image();
    logoImg.decoding = 'async';
    logoImg.src = url;
    logoImg.onload = function () { tint('#ffffff'); tint('#6b6e76'); };
  }
  if ('requestIdleCallback' in window) requestIdleCallback(prepare, { timeout: 3000 }); else setTimeout(prepare, 1500);

  function burst(btn) {
    var now = Date.now();
    if (now - last < 250) return;
    last = now;
    prepare();
    var dark = document.documentElement.getAttribute('data-beybling-theme') === 'dark';
    var src = tint(dark ? '#ffffff' : '#6b6e76');
    if (!src) { src = logoUrl(); if (!src) return; }
    var rect = btn.getBoundingClientRect();
    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;
    var host = btn.closest('dialog') || document.body;
    var layer = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.style.cssText = 'position:fixed;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:2147483000;overflow:visible;-webkit-transform:translateZ(0);transform:translateZ(0)';
    host.appendChild(layer);
    var phone = window.innerWidth < 750;
    var count = phone ? 9 : 14;
    var running = 0;
    // Start next frame so the click handler itself returns immediately (keeps the tap feeling instant).
    (function (run) { var done = false; function go() { if (done) return; done = true; run(); } requestAnimationFrame(go); setTimeout(go, 80); })(function () {
      var frag = document.createDocumentFragment();
      var parts = [];
      for (var i = 0; i < count; i++) {
        var size = 12 + Math.random() * (phone ? 10 : 14);
        var p = document.createElement('img');
        p.src = src;
        p.alt = '';
        p.decoding = 'async';
        p.draggable = false;
        p.style.cssText = 'position:absolute;left:' + (cx - size / 2) + 'px;top:' + (cy - size / 2) + 'px;width:' + size + 'px;height:' + size + 'px;opacity:0;will-change:transform,opacity;backface-visibility:hidden;';
        frag.appendChild(p);
        parts.push(p);
      }
      layer.appendChild(frag);
      parts.forEach(function (p, i) {
        var angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
        var dist = (phone ? 38 : 46) + Math.random() * (phone ? 44 : 62);
        var dx = Math.cos(angle) * dist;
        var dy = Math.sin(angle) * dist - 12;
        var rot = (Math.random() < 0.5 ? -1 : 1) * (150 + Math.random() * 270);
        running++;
        var a = p.animate(
          [
            { transform: 'translate3d(0,0,0) rotate(0deg) scale(0.3)', opacity: 1 },
            { transform: 'translate3d(' + dx * 0.75 + 'px,' + dy * 0.75 + 'px,0) rotate(' + rot * 0.6 + 'deg) scale(1)', opacity: 1, offset: 0.5 },
            { transform: 'translate3d(' + dx + 'px,' + (dy + 22) + 'px,0) rotate(' + rot + 'deg) scale(0.75)', opacity: 0 }
          ],
          { duration: 600 + Math.random() * 220, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)', fill: 'forwards' }
        );
        a.onfinish = function () { if (--running === 0) layer.remove(); };
      });
    });
    setTimeout(function () { layer.remove(); }, 1500);
  }

  document.addEventListener('click', function (event) {
    var btn = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    burst(btn);
    // Shop links navigate away immediately, which would cut the confetti off, so hold the navigation for a moment.
    var href = btn.tagName === 'A' ? btn.getAttribute('href') : '';
    if (href && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && btn.target !== '_blank') {
      event.preventDefault();
      setTimeout(function () { window.location.href = btn.href; }, 380);
    }
  }, true);
})();


/* bb-cart-upsell: "Add" button on the Beyblade Adapters offer in the cart drawer */
(function () {
  if (window.__bbCartUpsell) return;
  window.__bbCartUpsell = true;
  document.addEventListener('click', function (event) {
    var btn = event.target instanceof Element ? event.target.closest('[data-bb-upsell-add]') : null;
    if (!btn || btn.disabled) return;
    event.preventDefault();
    var variantId = Number(btn.getAttribute('data-variant-id'));
    if (!variantId) return;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding...';
    var sectionIds = Array.prototype.slice.call(document.querySelectorAll('cart-items-component'))
      .map(function (c) { return c.dataset.sectionId; })
      .filter(Boolean)
      .join(',');
    var body = { items: [{ id: variantId, quantity: 1 }] };
    if (sectionIds) { body.sections = sectionIds; body.sections_url = window.location.pathname; }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
      .then(function (out) {
        if (!out.ok) throw new Error('add failed');
        return import('@theme/events').then(function (mod) {
          btn.dispatchEvent(new mod.CartAddEvent({}, String(variantId), {
            source: 'bb-upsell',
            itemCount: 1,
            sections: out.data.sections
          }));
        }).catch(function () { window.location.reload(); });
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = label;
      });
  });
})();
